import React from 'react'

import { useToast } from 'containers/ToastManager'
import { ModalInjectedProps } from 'containers/ModalManager'

import Modal from 'components/Modal'
import { MemberCard } from '../../components/MemberCard'
import Button from 'components/Button'

import {
  useUpdateWorkspaceMemberMutation,
  WorkspaceMembershipFragment,
} from 'gqlTypes'

import { getMemberEmail } from '../../utils'
import { ModalBody, SectionTitle } from './EditorDowngradeModal.styles'

export interface EditorDowngradeModalProps extends ModalInjectedProps {
  member: WorkspaceMembershipFragment
}

export const EditorDowngradeModal: React.FC<EditorDowngradeModalProps> = props => {
  const { member, hideModal } = props
  const { showToast } = useToast()

  const memberName = member.user?.name
  const memberEmail = getMemberEmail(member)

  const [downgradeMember, { loading }] = useUpdateWorkspaceMemberMutation({
    variables: {
      input: {
        membershipIdentifier: member.identifier,
        accessLevel: 'VIEWER',
        /**
         * If the user is a guest we need to move it into a member,
         * if it's not a guest then we should keep the same role
         */
        role: member.role === 'GUEST' ? 'MEMBER' : undefined,
      },
    },
    onError: 'show-toast',
    onCompleted: () => {
      if (member.user?.name) {
        showToast(`${memberName} (${memberEmail}) was changed to Viewer.`)
      } else {
        showToast(`${memberEmail} was changed to Viewer.`)
      }

      hideModal()
    },
  })

  const memberNameOrEmail = member.user?.name ?? member.invite?.email

  return (
    <Modal title="Change to Viewer" onCancel={hideModal}>
      <ModalBody>
        <p>
          Viewer accounts are free for a Workspace. Viewers can browse, inspect
          and comment in the Sketch web app.
        </p>
        <p>
          {memberNameOrEmail} may still have private Drafts in this Workspace,
          which they will no longer be able to move or modify. Make sure they
          back up their work before changing them to a Viewer.
        </p>
        <MemberCard.BackgroundWrapper>
          <MemberCard
            name={memberName}
            email={memberEmail!}
            avatarSrc={member?.user?.avatar?.small}
          />
        </MemberCard.BackgroundWrapper>
      </ModalBody>
      {member.role !== 'GUEST' && member.isEditor && (
        <ModalBody>
          <SectionTitle>Updates to Your Next Bill</SectionTitle>
          <br />
          Changing this Member to a Viewer will reduce the amount due in your
          next bill. You can find the updated amount in your Plan Overview.
        </ModalBody>
      )}
      <Modal.Footer>
        <Button disabled={loading} onClick={hideModal}>
          Cancel
        </Button>
        <Button
          onClick={() => downgradeMember()}
          loading={loading}
          variant="primary">
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
